/**
 * 连接诊断工具 — 延迟探测 + 丢包分析 + 瓶颈定位
 */

export interface DiagnosticResult {
  avgLatency: number
  minLatency: number
  maxLatency: number
  jitter: number
  packetLoss: number
  bandwidth: number
  bottleneck: string | null
  score: number
}

export interface ProbeResult {
  sent: number
  received: number
  latencies: number[]
}

export class ConnectionDiagnostics {
  private probeResults: ProbeResult[] = []
  private bandwidthEstimate = 0

  /** 记录探测结果 */
  recordProbe(sent: number, received: number, latencies: number[]): void {
    this.probeResults.push({ sent, received, latencies })
  }

  /** 设置带宽估计 */
  setBandwidth(bytesPerSecond: number): void {
    this.bandwidthEstimate = bytesPerSecond
  }

  /** 运行诊断分析 */
  analyze(): DiagnosticResult {
    const allLatencies = this.probeResults.flatMap((p) => p.latencies)
    const totalSent = this.probeResults.reduce((s, p) => s + p.sent, 0)
    const totalReceived = this.probeResults.reduce((s, p) => s + p.received, 0)

    const avgLatency = allLatencies.length > 0 ? allLatencies.reduce((s, l) => s + l, 0) / allLatencies.length : 0
    const minLatency = allLatencies.length > 0 ? Math.min(...allLatencies) : 0
    const maxLatency = allLatencies.length > 0 ? Math.max(...allLatencies) : 0
    const jitter = allLatencies.length > 1 ? this.calcJitter(allLatencies) : 0
    const packetLoss = totalSent > 0 ? (totalSent - totalReceived) / totalSent : 0

    const bottleneck = this.detectBottleneck(avgLatency, packetLoss, jitter)
    const score = this.calcScore(avgLatency, packetLoss, jitter)

    return { avgLatency, minLatency, maxLatency, jitter, packetLoss, bandwidth: this.bandwidthEstimate, bottleneck, score }
  }

  /** 快速健康检查 */
  quickCheck(): { healthy: boolean; issues: string[] } {
    const result = this.analyze()
    const issues: string[] = []
    if (result.avgLatency > 200) issues.push('high-latency')
    if (result.packetLoss > 0.05) issues.push('packet-loss')
    if (result.jitter > 50) issues.push('high-jitter')
    return { healthy: issues.length === 0, issues }
  }

  /** 清空 */
  clear(): void {
    this.probeResults = []
    this.bandwidthEstimate = 0
  }

  private calcJitter(latencies: number[]): number {
    let sum = 0
    for (let i = 1; i < latencies.length; i++) sum += Math.abs(latencies[i] - latencies[i - 1])
    return sum / (latencies.length - 1)
  }

  private detectBottleneck(avgLatency: number, loss: number, jitter: number): string | null {
    if (loss > 0.1) return 'network-congestion'
    if (avgLatency > 500) return 'high-latency-route'
    if (jitter > 100) return 'unstable-connection'
    if (this.bandwidthEstimate > 0 && this.bandwidthEstimate < 1024 * 100) return 'low-bandwidth'
    return null
  }

  private calcScore(avgLatency: number, loss: number, jitter: number): number {
    let score = 100
    score -= Math.min(40, avgLatency / 10)
    score -= Math.min(30, loss * 300)
    score -= Math.min(20, jitter / 5)
    return Math.max(0, Math.round(score))
  }
}
